import { useEffect, useState } from 'react'
import type { Requirement, RequirementNode, RequirementSection } from '@ispot/shared'
import {
  REQUIREMENT_DOC_SECTIONS,
  REQUIREMENT_DOC_SECTION_LABELS,
  getRequirementSection,
} from '@ispot/shared'
import { computeDocumentStats } from './computeDocumentStats'

interface Props {
  /** Tree built by useRequirements.buildTree — already sorted by sortOrder. */
  tree: RequirementNode[]
  /** Flat list of (scoped) requirements — used to compute stats below the outline. */
  requirements: Requirement[]
}

/**
 * Left rail for Document view. Combines two roles in one column:
 *   1. On-this-page outline (primary) — clickable hierarchy, scroll-tracked.
 *   2. At-a-glance stats (secondary) — completeness, priority, status counts.
 *
 * Replaces the old DocumentSidekick right panel so the document body
 * gets the full width of the workspace.
 */
export function DocumentTOC({ tree, requirements }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const stats = computeDocumentStats(requirements)

  const byId = new Map(requirements.map(r => [r.id, r] as const))
  const grouped = groupTreeBySection(tree, byId)
  const flat = flatten(tree)

  useEffect(() => {
    if (flat.length === 0) return
    const targets = flat
      .map(({ id }) => document.querySelector<HTMLElement>(`[data-req-id="${id}"]`))
      .filter((el): el is HTMLElement => Boolean(el))
    if (targets.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) {
          const id = visible[0].target.getAttribute('data-req-id')
          if (id) setActiveId(id)
        }
      },
      { rootMargin: '-15% 0% -70% 0%', threshold: 0 },
    )
    targets.forEach(t => observer.observe(t))
    return () => observer.disconnect()
  }, [flat.map(f => f.id).join(',')])

  function jumpTo(id: string) {
    const el = document.querySelector<HTMLElement>(`[data-req-id="${id}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setActiveId(id)
  }

  return (
    <aside
      className="flex-shrink-0 overflow-y-auto py-4 pl-4 pr-2"
      style={{
        width: 256,
        height: '100%',
        borderRight: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
      }}
      aria-label="Document outline and overview"
    >
      <SectionLabel>On this page</SectionLabel>
      {flat.length === 0 && (
        <div className="text-[12px] px-1" style={{ color: 'var(--color-fg-muted)' }}>
          Nothing here yet — add an epic to get started.
        </div>
      )}
      {grouped.map((g, i) => (
        <div key={g.section} className={i === 0 ? '' : 'mt-3'}>
          <DocSectionHeading label={REQUIREMENT_DOC_SECTION_LABELS[g.section]} count={g.entries.length} />
          {g.entries.map(entry => (
            <TOCEntry
              key={entry.id}
              entry={entry}
              active={entry.id === activeId}
              onClick={() => jumpTo(entry.id)}
            />
          ))}
        </div>
      ))}

      {requirements.length > 0 && (
        <DocumentSummary stats={stats} />
      )}
    </aside>
  )
}

// ============================================================================
// DOCUMENT SUMMARY — collapsed by default; click to expand stats sections.
// Keeps the rail outline-first while preserving the dashboard for reviewers.
// ============================================================================

function DocumentSummary({ stats }: { stats: ReturnType<typeof computeDocumentStats> }) {
  const [open, setOpen] = useState(false)
  const pct = Math.round(stats.completenessAvg * 100)
  const tone =
    pct >= 80 ? 'var(--color-success)'
    : pct >= 50 ? 'var(--color-warning)'
    : 'var(--color-warm-500)'

  return (
    <div className="mt-8 pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-1 py-1 rounded text-left cursor-pointer border-0 bg-transparent transition-colors"
        style={{ fontSize: 11.5, color: 'var(--color-fg-secondary)' }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <span
          aria-hidden
          style={{
            width: 8, height: 8, borderRadius: 999,
            background: tone, flexShrink: 0,
          }}
        />
        <span className="tabular-nums" style={{ fontWeight: 600, color: 'var(--color-fg)' }}>
          {pct}%
        </span>
        <span style={{ color: 'var(--color-fg-muted)' }}>complete</span>
        <span className="flex-1" />
        <span aria-hidden style={{ color: 'var(--color-fg-muted)', opacity: 0.6, fontSize: 10 }}>
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <div className="mt-3">
          <SectionLabel>Priority</SectionLabel>
          <BreakdownList rows={stats.priorityRows} total={stats.priorityTotal} />

          <SectionLabel className="mt-4">Status</SectionLabel>
          <BreakdownList rows={stats.statusRows} total={stats.statusTotal} />

          <SectionLabel className="mt-4">Counts</SectionLabel>
          <CountsList byType={stats.byType} />
        </div>
      )}
    </div>
  )
}

// ============================================================================
// SECTION GROUPING
// ============================================================================

interface SectionGroup {
  section: RequirementSection
  entries: FlatEntry[]
}

/** Group flat tree entries under their epic's document section. */
function groupTreeBySection(
  tree: RequirementNode[],
  byId: Map<string, Requirement>,
): SectionGroup[] {
  const buckets = new Map<RequirementSection, FlatEntry[]>()
  for (const s of REQUIREMENT_DOC_SECTIONS) buckets.set(s, [])

  for (const epic of tree) {
    const section = getRequirementSection(epic, byId)
    const epicEntry: FlatEntry = {
      id: epic.id,
      name: epic.name || 'Untitled',
      depth: 0,
      priority: epic.priority,
      draft: !epic.name,
    }
    buckets.get(section)!.push(epicEntry)
    for (const story of epic.children) {
      buckets.get(section)!.push({
        id: story.id,
        name: story.name || 'Untitled',
        depth: 1,
        priority: story.priority,
        draft: !story.name,
      })
    }
  }

  return REQUIREMENT_DOC_SECTIONS
    .map(s => ({ section: s, entries: buckets.get(s)! }))
    .filter(g => g.entries.length > 0)
}

function DocSectionHeading({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-baseline justify-between px-1 mb-1">
      <span
        className="font-display"
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--color-fg)',
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </span>
      <span
        className="tabular-nums"
        style={{ fontSize: 10, color: 'var(--color-fg-muted)' }}
      >
        {count}
      </span>
    </div>
  )
}

// ============================================================================
// SECTION LABELS / DIVIDER
// ============================================================================

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`font-mono mb-2 px-1 ${className ?? ''}`}
      style={{
        fontSize: 10,
        color: 'var(--color-fg-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}
    >
      {children}
    </div>
  )
}

// ============================================================================
// OUTLINE ENTRIES
// ============================================================================

interface FlatEntry {
  id: string
  name: string
  depth: 0 | 1 | 2
  priority: Requirement['priority']
  /** True when the underlying record has no name yet — render as a quiet draft. */
  draft: boolean
}

function flatten(tree: RequirementNode[]): FlatEntry[] {
  const out: FlatEntry[] = []
  function visit(nodes: RequirementNode[]) {
    for (const n of nodes) {
      const depth = n.depth >= 2 ? 2 : (n.depth as 0 | 1)
      const draft = !n.name
      out.push({ id: n.id, name: n.name || 'Untitled', depth, priority: n.priority, draft })
      if (n.children.length > 0) visit(n.children)
    }
  }
  visit(tree)
  return out
}

function TOCEntry({
  entry, active, onClick,
}: {
  entry: FlatEntry; active: boolean; onClick: () => void
}) {
  const baseStyle: React.CSSProperties = {
    background: active ? 'var(--color-warm-100)' : 'transparent',
    border: 0,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'background 120ms cubic-bezier(0.2,0,0,1)',
  }

  // Drafts (no name yet) read as quiet placeholders, not first-class entries:
  // italic + muted color across every depth, regardless of active state.
  const draftStyle: React.CSSProperties = entry.draft
    ? { fontStyle: 'italic', color: 'var(--color-fg-muted)', fontWeight: 400 }
    : {}

  if (entry.depth === 0) {
    return (
      <button
        onClick={onClick}
        className="rounded px-1.5 py-1 mt-3 first:mt-0 block"
        style={{
          ...baseStyle,
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 12.5,
          letterSpacing: '-0.005em',
          color: 'var(--color-fg)',
          ...draftStyle,
        }}
      >
        {entry.name}
      </button>
    )
  }

  if (entry.depth === 1) {
    return (
      <button
        onClick={onClick}
        className="rounded px-1.5 py-1 block"
        style={{
          ...baseStyle,
          paddingLeft: 14,
          fontWeight: 500,
          fontSize: 12,
          color: active ? 'var(--color-fg)' : 'var(--color-fg-secondary)',
          ...draftStyle,
        }}
      >
        {entry.name}
      </button>
    )
  }

  return (
    <button
      onClick={onClick}
      className="rounded px-1.5 py-1 flex items-center gap-1.5"
      style={{
        ...baseStyle,
        paddingLeft: 26,
        fontSize: 11.5,
        color: active ? 'var(--color-fg)' : 'var(--color-fg-muted)',
        ...draftStyle,
      }}
    >
      <PriorityDot priority={entry.priority} />
      <span className="flex-1 min-w-0 truncate">{entry.name}</span>
    </button>
  )
}

function PriorityDot({ priority }: { priority: Requirement['priority'] }) {
  const color =
    priority === 'critical' ? 'var(--color-danger)'
    : priority === 'high'   ? 'var(--color-warning)'
    : priority === 'medium' ? 'var(--color-warm-600)'
    : priority === 'low'    ? 'var(--color-warm-500)'
    : 'var(--color-warm-300)'
  return (
    <span
      aria-hidden
      style={{ width: 6, height: 6, borderRadius: 999, background: color, flexShrink: 0 }}
    />
  )
}

// ============================================================================
// STATS — priority, status, counts (rendered inside DocumentSummary)
// ============================================================================

function BreakdownList({
  rows, total,
}: {
  rows: Array<{ label: string; count: number; color: string }>
  total: number
}) {
  if (total === 0) {
    return <div className="px-1" style={{ fontSize: 11, color: 'var(--color-fg-muted)' }}>—</div>
  }
  return (
    <ul className="list-none p-0 m-0 flex flex-col px-1" style={{ gap: 3 }}>
      {rows.filter(r => r.count > 0).map(r => {
        const pct = (r.count / total) * 100
        return (
          <li key={r.label} className="flex items-center gap-2">
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11.5, color: 'var(--color-fg-secondary)' }}>{r.label}</span>
            <span className="flex-1" />
            <span
              className="tabular-nums"
              style={{ fontSize: 10.5, color: 'var(--color-fg-muted)', fontFamily: 'var(--font-mono)' }}
            >
              {r.count} · {pct.toFixed(0)}%
            </span>
          </li>
        )
      })}
    </ul>
  )
}

function CountsList({
  byType,
}: { byType: Record<'epic' | 'story' | 'nfr-category', number> }) {
  const rows: Array<[string, number]> = [
    ['Epics',   byType.epic],
    ['Stories', byType.story],
    ['NFR',     byType['nfr-category']],
  ]
  const visible = rows.filter(([, n]) => n > 0)
  if (visible.length === 0) {
    return <div className="px-1" style={{ fontSize: 11, color: 'var(--color-fg-muted)' }}>—</div>
  }
  return (
    <ul className="list-none p-0 m-0 flex flex-col px-1" style={{ gap: 3 }}>
      {visible.map(([label, count]) => (
        <li key={label} className="flex items-center">
          <span style={{ fontSize: 11.5, color: 'var(--color-fg-secondary)' }}>{label}</span>
          <span className="flex-1" />
          <span
            className="tabular-nums"
            style={{ fontSize: 10.5, color: 'var(--color-fg-muted)', fontFamily: 'var(--font-mono)' }}
          >
            {count}
          </span>
        </li>
      ))}
    </ul>
  )
}
